import { useEffect } from 'react';
import { getTheme, isThemeId, onThemePicked, onThemeRowsChange, setTheme, type ThemeId } from './themes';

/** Where the signed-in user's theme is kept, given by the host. */
export interface ThemeAccount {
  /** The theme the account holds, if it holds one. */
  read(): Promise<string | undefined>;
  /** Keeps `theme` on the account. */
  save(theme: ThemeId): Promise<void>;
  /** Calls `listener` when the account's theme may have changed elsewhere; returns the unsubscribe. */
  onChange(listener: () => void): () => void;
}

/**
 * Keeps the theme on the signed-in user's account, so it follows them to every browser and anything acting for them can
 * set it. The browser's cached theme paints the page first, so there is no flash on landing; the account's theme then
 * applies if it differs (once the themes table has it), and every theme picked is saved back. A change made elsewhere
 * applies as soon as the account says so. `account` is null while no one is signed in, and should be made once per user.
 */
export function ThemeAccountSync({ account }: { account: ThemeAccount | null }) {
  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    let loaded = false;
    let pickedBeforeLoad = false;
    // The theme the account holds, as last read or written; any other theme picked is the user's, and is saved
    let saved: ThemeId | null = null;
    // The account's theme until the rows have it (the bundled defaults come before the table)
    let wanted: string | null = null;
    // Putting on the account's theme is not the user's pick
    let applying = false;

    // One save at a time, and only the latest pick waits: saves that overlapped could land in either order
    let saving: Promise<void> | null = null;
    let nextToSave: ThemeId | null = null;
    let picks = 0;

    const save = (theme: ThemeId) => {
      saved = theme;
      picks++;
      nextToSave = theme;
      saving ??= (async () => {
        while (nextToSave) {
          const next = nextToSave;
          nextToSave = null;
          await account.save(next).catch(error => console.error('Error saving the theme to the account', error));
        }
        saving = null;
      })();
    };

    // The account's theme put on once there is such a theme; setting it is a pick the account already holds
    const applyWanted = () => {
      if (!wanted || !isThemeId(wanted)) return;
      const theme = wanted;
      wanted = null;
      // The user picked one while the account's was read: theirs stands, and is saved over it once the read is done
      if (pickedBeforeLoad) return;
      saved = theme;
      if (theme === getTheme()) return;
      applying = true;
      try {
        setTheme(theme);
      } finally {
        applying = false;
      }
    };

    const stopPicks = onThemePicked(theme => {
      if (applying) return;
      if (!loaded) pickedBeforeLoad = true;
      else if (theme !== saved) save(theme);
    });
    const stopRows = onThemeRowsChange(applyWanted);

    // A read that overlaps a save may still hold the theme from before it
    const readAccountTheme = async () => {
      if (saving) await saving;
      const picksAtRead = picks;
      const theme = await account.read();
      if (cancelled || saving || picks !== picksAtRead || theme === undefined || theme === saved) return;
      wanted = theme;
      applyWanted();
    };

    readAccountTheme()
      .then(() => {
        loaded = true;
        // The user picked one while it loaded: that is the one to keep
        if (pickedBeforeLoad && !cancelled) save(getTheme());
        pickedBeforeLoad = false;
      })
      .catch(error => console.error('Error loading the theme from the account', error));

    const stopFollowing = account.onChange(() => {
      readAccountTheme().catch(error => console.error('Error loading the theme from the account', error));
    });

    return () => {
      cancelled = true;
      stopPicks();
      stopRows();
      stopFollowing();
    };
  }, [account]);

  return null;
}
